import { create } from 'zustand';

/**
 * User-controlled UI preferences. Loaded eagerly so first paint matches
 * the saved theme / motion settings before React mounts.
 */

const STORAGE_KEY = 'paas_settings';

export type ThemePref = 'system' | 'light' | 'dark';

export interface SettingsState {
  theme: ThemePref;
  /** Push-notification opt-in. Native only; web shows nothing. */
  pushEnabled: boolean;
  /** Biometric-unlock opt-in. Native only. */
  biometricEnabled: boolean;
  /** Honor `prefers-reduced-motion` regardless of OS setting. */
  reducedMotion: boolean;

  setTheme: (theme: ThemePref) => void;
  setPushEnabled: (enabled: boolean) => void;
  setBiometricEnabled: (enabled: boolean) => void;
  setReducedMotion: (enabled: boolean) => void;

  applyTheme: () => void;
  hydrate: () => void;
}

function persist(state: Pick<SettingsState, 'theme' | 'pushEnabled' | 'biometricEnabled' | 'reducedMotion'>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[settings] persist failed:', error);
  }
}

function resolveDarkClass(theme: ThemePref): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  // Fancourt is a light, cream design — default to Daylight, not the OS
  // preference. Estate Night stays available as an explicit opt-in.
  theme: 'light',
  pushEnabled: false,
  biometricEnabled: false,
  reducedMotion: false,

  setTheme(theme) {
    set({ theme });
    persist({ ...get(), theme });
    get().applyTheme();
  },
  setPushEnabled(pushEnabled) {
    set({ pushEnabled });
    persist({ ...get(), pushEnabled });
  },
  setBiometricEnabled(biometricEnabled) {
    set({ biometricEnabled });
    persist({ ...get(), biometricEnabled });
  },
  setReducedMotion(reducedMotion) {
    set({ reducedMotion });
    persist({ ...get(), reducedMotion });
  },

  applyTheme() {
    if (typeof document === 'undefined') return;
    const isDark = resolveDarkClass(get().theme);
    document.documentElement.classList.toggle('dark', isDark);
  },

  hydrate() {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<SettingsState>;
        set({
          theme: parsed.theme ?? 'light',
          pushEnabled: parsed.pushEnabled ?? false,
          biometricEnabled: parsed.biometricEnabled ?? false,
          reducedMotion: parsed.reducedMotion ?? false,
        });
      }
    } catch (error) {
      console.warn('[settings] hydrate failed:', error);
    }
    get().applyTheme();
  },
}));

/** Hydrate eagerly so dark class lands before first paint. */
useSettingsStore.getState().hydrate();
